import {
  Issue,
  IssueCreationSource,
  IssueMessageType,
  Message,
  useCurrentAccount,
} from '@gripp/shared-logic';
import * as Haptics from 'expo-haptics';
import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Platform,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { Menu, Text } from 'react-native-paper';
import { runOnJS, useSharedValue } from 'react-native-reanimated';
import { Colors } from '../../themes';
import { AccountAvatar } from '../accountAvatar';
import { Icon } from '../crop-circle/icons';
import { tokens } from '../crop-circle/tokens';
import { colors as CropCircleColors } from '../crop-circle/tokens/colors';
import { LocationText } from '../locationText';
import { AccessRequestMessage } from './accessRequestMessage';
import {
  AnonymousId,
  FunctionId,
  getIssueFullName,
  getIssueMessageHeader,
  getIssueMessageTextStyle,
} from './issueFormatUtils';
import { NewReaction } from './reaction/newReaction';
import { ReactionList } from './reaction/reactionList';
import { ReminderMessage } from './reminderMessage';
import { RoutineExceptionMessage } from './routineExceptionMessage';
import { RoutineMessage } from './routineMessage';
import { RoutinePromptMessage } from './routinePromptMessage';
import { MessageComponent } from './types';
import { UserMessage } from './userMessage';

export type IssueMessageProps = {
  message: Message;
  mediaUrl: string;
  maxImageWidth?: number;
  isUnread: boolean;
  issue: Issue;
  isFirstMessage: boolean;
  onImagePress?: (mediaId: string) => void;
  /**
   * When provided AND the message author is a real, callable account (not the
   * current user, not the anonymous/system accounts), an overflow (⋯) menu with
   * a "Call" action appears next to the author. Gated upstream by `call.create`.
   */
  onCall?: (actor: { id: string; name: string }) => void;
};

const MessageComponents: Record<IssueMessageType, MessageComponent> = {
  [IssueMessageType.AccessRequestMessage]: AccessRequestMessage,
  [IssueMessageType.AccessRequestResolved]: UserMessage,
  [IssueMessageType.UserMessage]: UserMessage,
  [IssueMessageType.SystemMessage]: UserMessage,
  [IssueMessageType.SystemMessageReminderAlert]: UserMessage,
  [IssueMessageType.ReminderMessage]: ReminderMessage,
  [IssueMessageType.RoutineMessage]: RoutineMessage,
  [IssueMessageType.RoutinePromptMessage]: RoutinePromptMessage,
  [IssueMessageType.RoutineExceptionMessage]: RoutineExceptionMessage,
};

const UNREAD_HIGHLIGHT_TIME_MS = 3000;

export const IssueMessage = (props: IssueMessageProps) => {
  const MessageComponent = MessageComponents[props.message.type];
  const [reactionVisible, setReactionVisible] = useState(false);
  const anchorRef = useRef<View>(null);

  const maxImageWidth = props.maxImageWidth || 320;
  const imageDimensions = {
    width: maxImageWidth,
    height: maxImageWidth,
  };
  const message = props.message;
  const issue = props.issue;
  const header = getIssueMessageHeader(message);
  const textStyle = getIssueMessageTextStyle(message);
  const { account: currentAccount } = useCurrentAccount();

  // Per-message call affordance: only for a real, callable author that isn't the
  // current user or the anonymous/system accounts. Gated upstream by call.create
  // (the screen only passes `onCall` when the caller may place calls).
  const author = message.updatedBy;
  const callActor =
    props.onCall &&
    author?.id &&
    ![AnonymousId, FunctionId].includes(author.id) &&
    author.id !== currentAccount?.id
      ? { id: author.id, name: getIssueFullName(author.id, author.name ?? undefined) }
      : undefined;

  const [unreadStyle, setUnreadStyle] = useState(props.isUnread);
  // Web-only hover, tracked at the message-row level so the call affordance
  // can reveal itself the way it does in Gripp OS.
  const [hovered, setHovered] = useState(false);
  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
        }
      : {};

  useEffect(() => {
    if (props.isUnread) {
      setTimeout(() => {
        setUnreadStyle(false);
      }, UNREAD_HIGHLIGHT_TIME_MS);
    }
  }, []);

  const isTouchingReactions = useSharedValue(false);

  const handleLongPress = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setReactionVisible(true);
  };

  const longPressGesture = Gesture.LongPress()
    .minDuration(500)
    .enabled(Platform.OS !== 'web')
    .onStart(() => {
      if (!isTouchingReactions.value) {
        runOnJS(handleLongPress)();
      }
      isTouchingReactions.value = false;
    });

  const showCreatedFrom =
    (issue.createdFrom === IssueCreationSource.Scanner ||
      issue.createdFrom === IssueCreationSource.Anonymousweb) &&
    props.isFirstMessage;

  const messageContent = (
    <View
      ref={anchorRef}
      style={[styles.container, unreadStyle ? styles.unread : styles.read]}
      {...hoverProps}
    >
      <View style={styles.titleRow}>
        <AccountAvatar
          avatar={header.avatar}
          avatarIcon={header.avatarIcon}
          mediaUrl={props.mediaUrl}
          size={32}
          style={header.avatarIcon ? header.avatarIconStyle : {}}
          iconColor={header.avatarIconColor}
          hasLightbox
        />
        <View style={styles.nameArea}>
          <Text style={[styles.name, header.fontStyle]} numberOfLines={1}>
            {header.updatedBy}
          </Text>
        </View>
        <Text style={styles.time}>{header.updatedAt}</Text>
        {callActor && props.onCall && (
          <MessageCallMenu
            actor={callActor}
            onCall={props.onCall}
            hovered={hovered}
          />
        )}
      </View>
      {showCreatedFrom && (
        <LocationText
          location={issue.location}
          address={issue.createdFromAddress ?? ''}
        />
      )}
      <View style={styles.message}>
        <MessageComponent
          message={message}
          imageDimensions={imageDimensions}
          mediaUrl={props.mediaUrl}
          textStyle={textStyle}
          issue={props.issue}
          onImagePress={props.onImagePress}
        />
        {Platform.OS === 'android' ? (
          <View
            onTouchStart={() => {
              isTouchingReactions.value = true;
            }}
          >
            <ReactionList
              onOpenNewReaction={() => setReactionVisible(true)}
              issue={props.issue}
              message={message}
            />
          </View>
        ) : (
          <ReactionList
            onOpenNewReaction={() => setReactionVisible(true)}
            issue={props.issue}
            message={message}
          />
        )}
      </View>
    </View>
  );

  const newReaction = (
    <NewReaction
      onCloseReaction={() => setReactionVisible(false)}
      isReactionVisible={reactionVisible}
      issue={props.issue}
      message={message}
      anchorRef={anchorRef as React.RefObject<View>}
    />
  );

  if (Platform.OS === 'android') {
    return (
      <>
        <GestureDetector gesture={longPressGesture}>
          {messageContent}
        </GestureDetector>
        {newReaction}
      </>
    );
  }

  if (Platform.OS === 'ios') {
    return (
      <>
        <TouchableOpacity activeOpacity={1} onLongPress={handleLongPress}>
          {messageContent}
        </TouchableOpacity>
        {newReaction}
      </>
    );
  }

  return (
    <>
      {messageContent}
      {newReaction}
    </>
  );
};

/**
 * Per-message overflow menu shown next to the author, mirroring Gripp OS:
 * on row hover the trailing slot widens (sliding the timestamp left) and an
 * orange circle with white dots pops in; clicking opens a Crop Circle–styled
 * menu whose only action is "Call" (other actions are out of scope). On native
 * there is no hover, so the circle is always present as a touch target.
 */
const MessageCallMenu = ({
  actor,
  onCall,
  hovered,
}: {
  actor: { id: string; name: string };
  onCall: (actor: { id: string; name: string }) => void;
  hovered: boolean;
}) => {
  const [visible, setVisible] = useState(false);
  const isWeb = Platform.OS === 'web';

  // Present while hovered or while the menu is open; always present on native.
  const show = !isWeb || hovered || visible;

  // Two staged values: the slot width opens first, then the circle pops in.
  const slot = useRef(new Animated.Value(show ? 1 : 0)).current;
  const circle = useRef(new Animated.Value(show ? 1 : 0)).current;

  useEffect(() => {
    if (!isWeb) return;
    if (show) {
      Animated.parallel([
        Animated.timing(slot, {
          toValue: 1,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(circle, {
          toValue: 1,
          duration: 150,
          delay: 80,
          useNativeDriver: false,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(circle, {
          toValue: 0,
          duration: 120,
          useNativeDriver: false,
        }),
        Animated.timing(slot, {
          toValue: 0,
          duration: 200,
          useNativeDriver: false,
        }),
      ]).start();
    }
  }, [isWeb, show, slot, circle]);

  const slotWidth = isWeb
    ? slot.interpolate({ inputRange: [0, 1], outputRange: [0, 32] })
    : 32;
  const circleScale = isWeb
    ? circle.interpolate({ inputRange: [0, 1], outputRange: [0.6, 1] })
    : 1;

  const trigger = (
    <Animated.View style={[styles.callSlot, { width: slotWidth }]}>
      <Animated.View
        style={{ opacity: isWeb ? circle : 1, transform: [{ scale: circleScale }] }}
      >
        <Pressable
          onPress={() => setVisible(true)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={`More options for ${actor.name}`}
          style={({ pressed }) => [
            styles.callCircle,
            pressed && styles.callCirclePressed,
          ]}
        >
          <View style={styles.dotsRow}>
            <View style={styles.dot} />
            <View style={styles.dot} />
            <View style={styles.dot} />
          </View>
        </Pressable>
      </Animated.View>
    </Animated.View>
  );

  return (
    <Menu
      visible={visible}
      onDismiss={() => setVisible(false)}
      contentStyle={styles.menu}
      anchor={trigger}
    >
      <CallMenuRow
        onPress={() => {
          setVisible(false);
          onCall(actor);
        }}
      />
    </Menu>
  );
};

/**
 * A single Crop Circle menu row (icon + label). Mirrors Gripp OS's PopupItem:
 * 32px tall, 12px horizontal padding, 12px gap between a 16px icon and the
 * label, with a neutral highlight on hover. We render this instead of Paper's
 * `Menu.Item` because that reserves a wide fixed icon column, leaving an
 * oversized gap between the icon and the text.
 */
const CallMenuRow = ({ onPress }: { onPress: () => void }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel="Call"
      style={[styles.menuItem, hovered && styles.menuItemHovered]}
    >
      <Icon name="Call" size={16} color={tokens.colors.neutral[700]} />
      <Text style={styles.menuItemTitle}>Call</Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    padding: 16,
    gap: 8,
  },
  // Trailing slot that opens on hover (web) to make room for the circle,
  // sliding the timestamp left — mirrors Gripp OS.
  callSlot: {
    height: 28,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    overflow: 'hidden',
  },
  callCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.brand.core,
  },
  callCirclePressed: {
    opacity: 0.85,
  },
  dotsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2.5,
  },
  dot: {
    width: 2.8,
    height: 2.8,
    borderRadius: 1.4,
    backgroundColor: tokens.colors.brand.fullWhite,
  },
  // Crop Circle overflow menu surface: rounded-xl white card, neutral border,
  // soft shadow, 8px padding — matches the Gripp OS Popup.
  menu: {
    backgroundColor: tokens.colors.brand.white,
    minWidth: 200,
    borderRadius: tokens.borderRadius.xl,
    borderWidth: 1,
    borderColor: tokens.colors.neutral[300],
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.sm,
  },
  menuItem: {
    height: 32,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 12,
    borderRadius: tokens.borderRadius.sm,
  },
  menuItemHovered: {
    backgroundColor: tokens.colors.neutral[200],
  },
  menuItemTitle: {
    fontSize: 14,
    color: tokens.colors.neutral[700],
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    opacity: 0.8,
  },
  nameArea: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  name: {
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 18,
    color: Colors.black,
  },
  time: {
    color: CropCircleColors.slate[400],
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
  },
  message: {
    marginTop: 0,
  },
  messageText: {
    flexWrap: 'wrap',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 22,
  },
  read: {
    backgroundColor: Colors.brand.white,
  },
  unread: {
    backgroundColor: Colors.unreadBackground,
  },
  modalContainer: {
    backgroundColor: 'transparent',
  },
  modalContentContainer: {
    marginTop: 'auto',
    height: 500,
  },
});
